import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Youtube, Check, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { gradeSubjects } from "@/lib/subjects";
import { toast } from "sonner";
import LessonEditor from "@/components/admin/LessonEditor";

const GRADES = [1, 2, 3, 4, 5, 6];

export default function ManageVideos() {
  const [grade, setGrade] = useState("1");
  const [subject, setSubject] = useState(gradeSubjects(1)[0]);
  const subjects = gradeSubjects(grade);
  const pickGrade = (g) => { setGrade(g); if (!gradeSubjects(g).includes(subject)) setSubject(gradeSubjects(g)[0]); };
  const [chapters, setChapters] = useState([]);
  const [urls, setUrls] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState({});
  const [open, setOpen] = useState(null);

  const loadChapters = () => {
    setLoading(true);
    setOpen(null);
    api.get(`/admin/lessons/${grade}/${encodeURIComponent(subject)}`).then(({ data }) => setLessons(data.lessons)).catch(() => setLessons({}));
    api.get(`/review/chapters/${grade}/${encodeURIComponent(subject)}`)
      .then(({ data }) => {
        setChapters(data.chapters);
        const map = {};
        data.chapters.forEach((c) => { map[c.chapter_id] = c.video_url || ""; });
        setUrls(map);
      })
      .catch(() => setChapters([]))
      .finally(() => setLoading(false));
  };

  useEffect(loadChapters, [grade, subject]); // eslint-disable-line

  const save = async (c) => {
    setSavingId(c.chapter_id);
    try {
      await api.put("/review/video", {
        grade: Number(grade), subject, chapter_id: c.chapter_id, video_url: urls[c.chapter_id] || "",
      });
      toast.success(`Saved video for "${c.title}"`);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center gap-3">
          <Link to="/admin">
            <Button variant="ghost" className="rounded-full" data-testid="videos-back">
              <ArrowLeft size={18} className="mr-1" /> Admin
            </Button>
          </Link>
          <span className="font-bold flex items-center gap-2"><Youtube size={18} className="text-red-600" /> Chapter videos</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">
        <p className="text-slate-500 mb-4">Paste a YouTube link for the whole chapter, and/or open “Lesson videos” to add one video per lesson. Lessons without a link show “Coming soon” to children.</p>
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          <Select value={grade} onValueChange={pickGrade}>
            <SelectTrigger data-testid="videos-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => <SelectItem key={g} value={String(g)} data-testid={`videos-grade-${g}`}>Grade {g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger data-testid="videos-subject"><SelectValue>{subject}</SelectValue></SelectTrigger>
            <SelectContent>
              {subjects.map((s) => <SelectItem key={s} value={s} data-testid={`videos-subject-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
        ) : (
          <div className="mt-6 space-y-3">
            {chapters.map((c) => {
              const ls = lessons[c.chapter_id] || [];
              const withVideo = ls.filter((l) => l.video_id).length;
              return (
              <div key={c.chapter_id} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid={`video-row-${c.chapter_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 sm:w-72 shrink-0">
                  <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">{c.index + 1}</span>
                  <span className="font-semibold text-sm truncate">{c.title}</span>
                </div>
                <Input
                  placeholder="https://youtube.com/…"
                  value={urls[c.chapter_id] || ""}
                  onChange={(e) => setUrls((m) => ({ ...m, [c.chapter_id]: e.target.value }))}
                  className="flex-1"
                  data-testid={`video-input-${c.chapter_id}`}
                />
                {urls[c.chapter_id] ? (
                  <a href={urls[c.chapter_id]} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-red-600 shrink-0" title="Open link">
                    <ExternalLink size={18} />
                  </a>
                ) : null}
                <Button onClick={() => save(c)} disabled={savingId === c.chapter_id} className="rounded-full bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`video-save-${c.chapter_id}`}>
                  {savingId === c.chapter_id ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} className="mr-1" /> Save</>}
                </Button>
              </div>
              <button onClick={() => setOpen(open === c.chapter_id ? null : c.chapter_id)} data-testid={`lessons-toggle-${c.chapter_id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900">
                {open === c.chapter_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Lesson videos · {ls.length} lesson(s), {withVideo} with a video
              </button>
              {open === c.chapter_id && (
                <LessonEditor grade={grade} subject={subject} chapterId={c.chapter_id} initial={ls}
                  onSaved={(saved) => setLessons((m) => ({ ...m, [c.chapter_id]: saved }))} />
              )}
              </div>
              );
            })}
            {!chapters.length && <p className="text-slate-400 text-center py-8" data-testid="videos-empty">No chapters for this grade and subject yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
